import React, { useState, useEffect } from "react";
import { Box, Flex, HStack, VStack, Text, Grid, GridItem } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import Header from "../Header";
import StudentClassroomsTile from "./StudentClassroomsTile";
import StudentAssignedWorkTile from "./StudentAssignedWorkTile";
import ClassroomCard from "../ClassroomCard";
import StudentAssignmentCard from "./StudentAssignmentCard";
import AssignmentTileIcon from "../Images/assignment-completed-icon.svg";
import FeedbackReceievedIcon from "../Images/feedback-received-icon.svg";
import axios from "axios";
import { logout } from "../../Components/auth/logout";
import { c, useClassroomColorMode } from "../classroomColors";
import { formatExamBoard } from "../../Components/examBoards";
import "../classroom-theme.css";

const DashboardStudent = () => {
  const { isDark, toggleColorMode } = useClassroomColorMode();
  const [classrooms, setClassrooms] = useState([0]);
  const [allWork, setAllWork] = useState([]);

  // Advanced Filter States
  const [submissionFilter, setSubmissionFilter] = useState("ALL");
  const [boardFilter, setBoardFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEAR_FIRST"); // NEAR_FIRST, FAR_FIRST

  const [classroomsError, setClassroomsError]     = useState(null);
  const [assignedWorkError, setAssignedWorkError] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout({ navigate });
  };
  
  const token = `Bearer ${localStorage.getItem('accessToken')}`;

  useEffect(() => {
    axios
      .get("https://stand-ai-api-v1.onrender.com/api/v1/user-course-links/detail/?user_type=STUDENT&limit=100",
        {
          headers: {
            Authorization: token,
          },
        }
      )
      .then((response) => {
        setClassrooms(response.data);
        setClassroomsError(null);
      })
      .catch((err) => {
        console.error(err);
        setClassroomsError("There was an error loading your spaces.");
      })

    
      // Fetch every attempt once, then split into "assigned" vs "feedback
      // received" on the client by completion_status (see below). The backend
      // never flips manual_marking_status to COMPLETE, so filtering on it there
      // left everything in "Assigned Work".
      axios
      .get(
        "https://stand-ai-api-v1.onrender.com/api/v1/user-course-links/user-paper-attempts/detail/?course_user_type=STUDENT&limit=100",
        {
          headers: {
            Authorization: token,
          },
        }
      )
      .then((response) => {
        setAllWork(Array.isArray(response.data) ? response.data : []);
        setAssignedWorkError(null);
      })
      .catch((err) => {
        console.error(err);
        setAssignedWorkError("Could not load your assigned work.");
      });
    }, []);

  // A teacher can release feedback while this list is open, which would leave
  // the card sitting on "Awaiting Feedback" until a manual reload. Refetch when
  // the tab comes back into focus so released work moves across on its own.
  useEffect(() => {
    const refresh = async () => {
      try {
        const { data } = await axios.get(
          "https://stand-ai-api-v1.onrender.com/api/v1/user-course-links/user-paper-attempts/detail/?course_user_type=STUDENT&limit=100",
          { headers: { Authorization: token } }
        );
        if (Array.isArray(data)) setAllWork(data);
      } catch (err) {
        /* soft-fail: keep showing what we already have */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Filter Logic ---

  // An attempt is "feedback received" only once the teacher has reviewed and
  // released feedback. A submission that's merely complete (submitted) stays in
  // assigned work as "awaiting feedback" until then.
  const hasFeedback = (item) => item?.feedback_released === true;

  const workItems = Array.isArray(allWork) ? allWork : [];
  const feedbackReceived = workItems.filter(hasFeedback);

  // Get unique boards for filter options
  const uniqueBoards = [...new Set(workItems.map(item => item.exam_board_code))].filter(Boolean);

  const processedAssignedWork = workItems
    .filter(item => !hasFeedback(item))
    .filter(item => {
      // Submission status filter
      const subMatch = submissionFilter === "ALL" || item.submission_status === submissionFilter;
      // Board filter
      const boardMatch = boardFilter === "ALL" || item.exam_board_code === boardFilter;

      return subMatch && boardMatch;
    })
    .sort((a, b) => {
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return sortOrder === "NEAR_FIRST" ? dateA - dateB : dateB - dateA;
    });

  const assignedWorkFilterGroups = [
    {
      title: "Submission Status",
      options: [
        { label: "All Submissions", onClick: () => setSubmissionFilter("ALL"), isActive: submissionFilter === "ALL" },
        { label: "Submitted", onClick: () => setSubmissionFilter("SUBMITTED"), isActive: submissionFilter === "SUBMITTED" },
        { label: "Not Submitted", onClick: () => setSubmissionFilter("NOT_SUBMITTED"), isActive: submissionFilter === "NOT_SUBMITTED" },
      ]
    },
    {
      title: "Filter by Board",
      options: [
        { label: "All Boards", onClick: () => setBoardFilter("ALL"), isActive: boardFilter === "ALL" },
        ...uniqueBoards.map(board => ({
          label: formatExamBoard(board),
          onClick: () => setBoardFilter(board),
          isActive: boardFilter === board
        }))
      ]
    },
    {
      title: "Sort By",
      options: [
        { label: "Due Date (Soonest)", onClick: () => setSortOrder("NEAR_FIRST"), isActive: sortOrder === "NEAR_FIRST" },
        { label: "Due Date (Latest)", onClick: () => setSortOrder("FAR_FIRST"), isActive: sortOrder === "FAR_FIRST" },
      ]
    }
  ];

  return (
    <Box
      className="stande-classroom"
      data-theme={isDark ? "dark" : "light"}
      minHeight="100vh"
      bg={c.page}
      transition="background 0.2s"
    >
      <Box paddingLeft="75px" paddingRight="75px">
        <Header handleLogout={handleLogout} isDark={isDark} onToggleColorMode={toggleColorMode} />
      </Box>

      <Box padding="48px 75px">
        {/* Greeting Section */}
        <Flex justifyContent="space-between" alignItems="flex-end" mb="40px">
          <VStack align="flex-start" spacing="4px">
            <Text fontSize="28px" fontWeight="500" color={c.text}>
              Good Afternoon
            </Text>
            <Text fontSize="16px" color={c.textSub} fontWeight="300">
              Here's what's happening with your learning today.
            </Text>
          </VStack>
        </Flex>

        <Grid
          templateColumns={{ base: "1fr", lg: "repeat(12, 1fr)" }}
          gap="32px"
        >
          {/* Main Focus: Assigned Work */}
          <GridItem colSpan={{ base: 1, lg: 7 }}>
            <StudentAssignedWorkTile
              iconSrc={AssignmentTileIcon}
              label="Assigned Work"
              filterGroups={assignedWorkFilterGroups}
              content={
                processedAssignedWork.length === 0
                  ? (workItems.filter(i => !hasFeedback(i)).length === 0 ? "You have no assigned work coming up" : "No work matches your filters")
                  : processedAssignedWork.map((assigned, idx) => (
                        <StudentAssignmentCard
                          key={assigned.guid || idx}
                          userPaperAttemptGuid={assigned.guid}
                          completionStatus={assigned.completion_status}
                          submissionStatus={assigned.submission_status}
                          subjectCode={assigned.subject_code}
                          classroomName={assigned.course_name}
                          qualificationCode={assigned.exam_qualification_code}
                          boardName={assigned.exam_board_code}
                          assignmentName={assigned.paper_name}
                          dueDate={assigned.due_date}
                          subjectName={assigned.subject_name}
                          scoredMarks={assigned.total_scored_marks}
                          totalMarks={assigned.total_marks}
                          feedbackReleased={assigned.feedback_released}
                        />
                      ))
              }
            />
          </GridItem>

          {/* Side Content: Classrooms and Feedback */}
          <GridItem colSpan={{ base: 1, lg: 5 }}>
            <VStack spacing="32px" align="stretch">
              <StudentClassroomsTile
                content={
                  classrooms.length === 0 || classrooms[0] === 0 ? (
                    "You have not joined a course yet"
                  ) : (
                    <Flex direction="column" gap="12px" width="100%">
                      {classrooms.map((c) => (
                        <ClassroomCard
                          key={c.id}
                          subjectCode={c.subject_code}
                          classroomName={c.course_name}
                          qualificationCode={c.exam_qualification_code}
                          boardName={c.exam_board_code}
                          typeOfUser="STUDENT"
                          onClick={() => {
                            navigate("/classroom/student/class", {
                              state: {
                                courseGuid: c.course_guid,
                                classroomName: c.course_name,
                                userType: "STUDENT"
                              },
                            });
                          }}
                        />
                      ))}
                    </Flex>
                  )
                }
              />

              <StudentAssignedWorkTile
                iconSrc={FeedbackReceievedIcon}
                label="Feedback Received"
                content={
                  feedbackReceived.length === 0
                    ? "You have not received any feedback yet"
                    : feedbackReceived.map((assigned, idx) => (
                        <StudentAssignmentCard
                          key={assigned.guid || idx}
                          userPaperAttemptGuid={assigned.guid}
                          completionStatus={assigned.completion_status}
                          submissionStatus={assigned.submission_status}
                          subjectCode={assigned.subject_code}
                          classroomName={assigned.course_name}
                          qualificationCode={assigned.exam_qualification_code}
                          boardName={assigned.exam_board_code}
                          assignmentName={assigned.paper_name}
                          dueDate={assigned.due_date}
                          subjectName={assigned.subject_name}
                          scoredMarks={assigned.total_scored_marks}
                          totalMarks={assigned.total_marks}
                          feedbackReleased={assigned.feedback_released}
                        />
                      ))
                }
              />
            </VStack>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
};

export default DashboardStudent;
